import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/languageStore';
import { googleMapsLoader } from '../utils/googleMapsLoader';

// TypeScript declarations for Google Maps API
declare global {
    interface Window {
        google: any;
    }
}

export function GoogleMap() {
    const { t } = useLanguage();
    const mapRef = useRef(null);
    const googleMapRef = useRef(null);
    const [mapError, setMapError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Get locations from translations (dynamic based on available keys)
    const getLocations = (): Array<{
        name: string;
        lat: number;
        lng: number;
        address: string;
    }> => {
        const locations: Array<{
            name: string;
            lat: number;
            lng: number;
            address: string;
        }> = [];

        // Use a different approach - access translations directly from the i18n cache
        // We'll use the existing translation system but check for existence first
        const checkAndGetLocation = (locationNumber: number) => {
            const nameKey = `map.locations.location${locationNumber}.name`;
            const latKey = `map.locations.location${locationNumber}.lat`;
            const lngKey = `map.locations.location${locationNumber}.lng`;
            const addressKey = `map.locations.location${locationNumber}.address`;

            // Use silent mode to check if all keys exist
            const name = t(nameKey, true);
            const lat = t(latKey, true);
            const lng = t(lngKey, true);
            const address = t(addressKey, true);

            // If any key returns the same string (meaning translation not found), return null
            if (name === nameKey || lat === latKey || lng === lngKey || address === addressKey) {
                return null;
            }

            // Validate that lat and lng are valid numbers
            const latNum = parseFloat(lat);
            const lngNum = parseFloat(lng);
            if (isNaN(latNum) || isNaN(lngNum)) {
                console.warn(`Invalid coordinates for location ${locationNumber}: lat=${lat}, lng=${lng}`);
                return null;
            }

            return {
                name,
                lat: latNum,
                lng: lngNum,
                address
            };
        };

        // Start from location1 and keep going until we don't find any more
        let locationNumber = 1;
        const maxLocations = 10; // Reduced safety limit

        while (locationNumber <= maxLocations) {
            const location = checkAndGetLocation(locationNumber);
            if (location) {
                locations.push(location);
                locationNumber++;
            } else {
                // No more locations found, stop
                break;
            }
        }

        return locations;
    };

    const locations = getLocations();

    // Calculate map center
    const getMapCenter = () => {
        if (locations.length === 0) return { lat: 20, lng: 0 };
        const totalLat = locations.reduce((sum, loc) => sum + loc.lat, 0);
        const totalLng = locations.reduce((sum, loc) => sum + loc.lng, 0);
        return {
            lat: totalLat / locations.length,
            lng: totalLng / locations.length
        };
    };

    useEffect(() => {
        const initializeMap = async () => {
            try {
                setIsLoading(true);
                setMapError(null);

                // Use the centralized loader
                await googleMapsLoader.load();

                if (!mapRef.current) {
                    setMapError('Map container not available');
                    setIsLoading(false);
                    return;
                }

                if (!window.google || !window.google.maps) {
                    setMapError('Google Maps API not loaded properly');
                    setIsLoading(false);
                    return;
                }

                const mapCenter = getMapCenter();

                // Create map
                const map = new window.google.maps.Map(mapRef.current, {
                    center: mapCenter,
                    zoom: locations.length > 1 ? 2 : 10, // Zoom out for multiple locations
                    mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                    styles: [
                        // Custom styling (optional)
                        {
                            "featureType": "water",
                            "elementType": "geometry",
                            "stylers": [{ "color": "#e9e9e9" }, { "lightness": 17 }]
                        }
                    ]
                });

                googleMapRef.current = map;

                // Add markers for each location using AdvancedMarkerElement
                const bounds = new window.google.maps.LatLngBounds();
                const markers: any[] = [];

                for (const location of locations) {
                    try {
                        // Check if AdvancedMarkerElement is available, fallback to regular Marker
                        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
                            // Create AdvancedMarkerElement instead of deprecated Marker
                            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                                position: { lat: location.lat, lng: location.lng },
                                map: map,
                                title: location.name,
                            });

                            // Create info window for each marker
                            const infoWindow = new window.google.maps.InfoWindow({
                                content: `
                                    <div style="padding: 10px;">
                                        <h3 style="margin: 0 0 5px 0; color: var(--primary-color);">${location.name}</h3>
                                        <p style="margin: 0; color: #666;">${location.address}</p>
                                    </div>
                                `
                            });

                            // Add click listener
                            marker.addListener('click', () => {
                                infoWindow.open(map, marker);
                            });

                            markers.push(marker);
                            bounds.extend(marker.position);
                        } else {
                            // Fallback to regular Marker if AdvancedMarkerElement is not available
                            const marker = new window.google.maps.Marker({
                                position: { lat: location.lat, lng: location.lng },
                                map: map,
                                title: location.name,
                                animation: window.google.maps.Animation.DROP
                            });

                            // Create info window for each marker
                            const infoWindow = new window.google.maps.InfoWindow({
                                content: `
                                    <div style="padding: 10px;">
                                        <h3 style="margin: 0 0 5px 0; color: var(--primary-color);">${location.name}</h3>
                                        <p style="margin: 0; color: #666;">${location.address}</p>
                                    </div>
                                `
                            });

                            // Add click listener
                            marker.addListener('click', () => {
                                infoWindow.open(map, marker);
                            });

                            markers.push(marker);
                            bounds.extend(marker.getPosition());
                        }
                    } catch (markerError) {
                        console.warn(`Failed to create marker for ${location.name}:`, markerError);
                    }
                }

                // Fit map to show all markers
                if (locations.length > 1 && markers.length > 0) {
                    map.fitBounds(bounds);
                }

                setIsLoading(false);
            } catch (error) {
                console.error('Failed to initialize map:', error);
                setMapError('Failed to load map. Please try again later.');
                setIsLoading(false);
            }
        };

        initializeMap();
    }, [locations]);

    if (mapError) {
        return (
            <div className="w-full" style={{ direction: "ltr", textAlign: "center" }}>
                <h2 className="text-center font-bold text-2xl mb-4 tracking-tight">
                    {t("map.title")}
                </h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {mapError}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full" style={{ direction: "ltr", textAlign: "center" }}>
            <h2 className="text-center font-bold text-2xl mb-4 tracking-tight">
                {t("map.title")}
            </h2>

            <div style={{
                height: 400,
                width: "100%",
                borderRadius: 16,
                margin: "0 auto",
                overflow: "hidden",
                border: "1px solid #e9ecef",
                position: "relative"
            }}>
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 z-10">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600">Loading map...</p>
                        </div>
                    </div>
                )}
                <div
                    ref={mapRef}
                    style={{
                        height: '100%',
                        width: '100%'
                    }}
                />
            </div>

            {/* Office locations list
            {locations.length > 0 && (
                <div className="mt-6 bg-white p-4 rounded-lg shadow-lg max-w-2xl mx-auto">
                    <div className="font-bold mb-3 text-lg">{t("map.locations.title")}:</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {locations.map((location) => (
                            <div key={location.name} className="text-sm">
                                <div className="font-semibold text-primary">{location.name}</div>
                                <div className="text-gray-600">{location.address}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )} */}
        </div>
    );
}